import { useMemo } from 'react';
import { GroupBase } from 'react-select';

import { Option } from '@/react/components/form-components/PortainerSelect';
import { RegistryValue } from '@/react/kubernetes/helm/components/HelmRegistrySelect';
import { HelmRelease } from '@/react/kubernetes/helm/types';

import {
  detectChartReference,
  ChartReferenceDetectionResult,
} from './detectChartReference';
import { useIsChartSourceValid } from './useIsChartSourceValid';
import { useIsChartValid } from './useIsChartValid';

export interface ChartReferenceState extends ChartReferenceDetectionResult {
  source: ReturnType<typeof useIsChartSourceValid>;
  chart: ReturnType<typeof useIsChartValid>;
}

/**
 * Combines static chart-reference detection with dynamic reachability checks.
 */
export function useChartReferenceState(
  release: HelmRelease,
  registryOptions: GroupBase<Option<RegistryValue>>[]
): ChartReferenceState {
  const detection = useMemo(
    () => detectChartReference({ release, registryOptions }),
    [release, registryOptions]
  );

  // Check source/chart validity and clear values if invalid
  const source = useIsChartSourceValid(
    release.chartReference,
    detection.selectedRegistry
  );
  if (source.hasError) {
    detection.selectedRegistry = undefined;
    detection.requiresSourceConfirmation = false;
    // also clear the chart name as because it depends on the source
    detection.selectedChartName = undefined;
    detection.requiresChartConfirmation = false;
  }
  const chart = useIsChartValid(release.chartReference);
  if (chart.hasError) {
    detection.selectedChartName = undefined;
    detection.requiresChartConfirmation = false;
  }
  const infoAlert = pickInfoAlert(source, chart, detection.infoAlert);

  return {
    ...detection,
    infoAlert,
    source,
    chart,
  };
}

function pickInfoAlert(
  source: ReturnType<typeof useIsChartSourceValid>,
  chart: ReturnType<typeof useIsChartValid>,
  detectionMsg?: string
) {
  if (detectionMsg) return detectionMsg;
  if (source.hasError) return source.errorMessage;
  if (chart.hasError) return chart.errorMessage;
  return undefined;
}
